/**
 * Bascule d'un élève DÉJÀ lié en « étudiant alternant » par son tuteur, dans sa
 * relation avec lui (RPC `tutor_set_student_alternant`, migration 0250 —
 * postérieure aux types générés → client générique, calque `rendezvous.ts`).
 *
 * Les gardes (tutor_links actif, mentorat académique activé, tuteur académique
 * actuel) vivent côté SQL : ce module ne fait que confirmer avec le tuteur,
 * enchaîner l'activation du mentorat si besoin, appeler la RPC et traduire les
 * erreurs.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useOffersAcademicMentoring, useSetOffersAcademicMentoring } from '@/lib/tutor/rendezvous';

const db = supabase as unknown as SupabaseClient;

const ALTERNANT_ERROR_LABELS: Record<string, string> = {
  not_authenticated: 'Connecte-toi pour continuer.',
  not_a_tutor: 'Seul un tuteur peut faire ce changement.',
  student_not_found: 'Profil introuvable.',
  not_linked_student: "Cet élève n'est pas (ou plus) relié à toi.",
  academic_mentoring_disabled: "Active d'abord ton mentorat académique.",
  academic_tutor_already_set: 'Cet étudiant a déjà choisi un autre tuteur académique.',
  not_academic_tutor_of_student: "Tu n'es pas le tuteur académique de cet étudiant.",
};

/** Traduit le message brut d'une erreur RPC (code Postgres) en texte affichable. */
export function translateAlternantError(rawMessage: string): string {
  return ALTERNANT_ERROR_LABELS[rawMessage] ?? 'Une erreur est survenue. Réessaie plus tard.';
}

/** Texte de confirmation avant de passer un élève en étudiant alternant. */
export function toAlternantConfirmMessage(studentName: string, needsMentoringOptIn: boolean) {
  const base =
    `Passer ${studentName} en étudiant alternant ?\n\n` +
    `Il/elle rejoindra « Mes étudiants alternants » et pourra avoir des entretiens ` +
    `académiques gratuits avec toi.`;
  return needsMentoringOptIn
    ? `${base}\n\nCela active aussi ton mentorat académique : tu apparaîtras dans la liste des tuteurs académiques.`
    : base;
}

/**
 * Texte de confirmation avant de retirer le statut. `declared` = l'étudiant
 * s'est lui-même déclaré alternant (il garde ce statut, il est seulement
 * détaché de ce tuteur) ; sinon c'est un élève basculé, qui redevient élève.
 */
export function backToStudentConfirmMessage(studentName: string, declared: boolean) {
  return declared
    ? `Retirer ${studentName} de tes étudiants alternants ?\n\nIl/elle reste alternant·e mais n'est plus rattaché·e à toi comme tuteur académique. Les entretiens déjà programmés sont conservés.`
    : `Repasser ${studentName} en élève ?\n\nIl/elle quitte « Mes étudiants alternants ». Les entretiens déjà programmés sont conservés.`;
}

/** Appelle la RPC (p_alternant=true : bascule / rattache ; false : retire). */
export function useSetStudentAlternant(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { studentId: string; alternant: boolean }) => {
      const { error } = await db.rpc('tutor_set_student_alternant', {
        p_student_id: input.studentId,
        p_alternant: input.alternant,
      });
      if (error) throw new Error(error.message);
    },
    // onSettled (pas onSuccess) : l'état réel prime même si la RPC a échoué.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tutor-students', tutorId] });
      void qc.invalidateQueries({ queryKey: ['academic-mentees', tutorId] });
      void qc.invalidateQueries({ queryKey: ['tutor-student-detail'] });
    },
  });
}

export interface AlternantSwitchTarget {
  studentId: string;
  name: string;
}

/**
 * Actions « Passer en étudiant alternant » / « Repasser en élève » avec
 * confirmation. Si le tuteur n'a pas encore activé le mentorat académique, la
 * confirmation le dit explicitement (il devient visible dans la liste des
 * tuteurs académiques) puis l'active avant la bascule — jamais silencieusement.
 */
export function useAlternantSwitch(tutorId: string) {
  const { data: offersMentoring } = useOffersAcademicMentoring(tutorId);
  const setOffers = useSetOffersAcademicMentoring(tutorId);
  const setAlternant = useSetStudentAlternant(tutorId);
  const [error, setError] = useState<string | null>(null);

  async function run(confirmMessage: string, studentId: string, alternant: boolean, optIn = false) {
    if (!window.confirm(confirmMessage)) return;
    setError(null);
    try {
      if (optIn) await setOffers.mutateAsync(true);
      await setAlternant.mutateAsync({ studentId, alternant });
    } catch (err) {
      setError(translateAlternantError(err instanceof Error ? err.message : ''));
    }
  }

  return {
    error,
    isPending: setAlternant.isPending || setOffers.isPending,
    toAlternant: (target: AlternantSwitchTarget) => {
      const needsOptIn = !offersMentoring;
      return run(
        toAlternantConfirmMessage(target.name, needsOptIn),
        target.studentId,
        true,
        needsOptIn,
      );
    },
    backToStudent: (target: AlternantSwitchTarget & { declared: boolean }) =>
      run(backToStudentConfirmMessage(target.name, target.declared), target.studentId, false),
  };
}
